import { useState } from 'react';
import { useEstadoDeLista } from '@titularis/ui';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router';
import type {
  ExamModule,
  IngestionRunRow,
} from '@titularis/api-client';
import { api } from '../api';
import { MODULO_LABEL, formatarData } from '../dominio';

/**
 * Cadastro Assistido: o módulo por si.
 *
 * **Deixou de acionar em 29/08/2026**, quando o cadastro passou para o Claude
 * Code: quem escolhe a prova e os campos é a ferramenta `abrir_prova`, na
 * máquina de quem opera. O que esta tela faz é acompanhar o que rodou em todas
 * as provas, sem abrir uma tela por prova, e desfazer o que não presta.
 */
const STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila',
  running: 'Processando',
  needs_confirmation: 'Aguardando confirmação',
  succeeded: 'Concluído',
  failed: 'Falhou',
};

const STATUS_COR: Record<string, string> = {
  queued: 'default',
  running: 'processing',
  needs_confirmation: 'warning',
  succeeded: 'green',
  failed: 'red',
};

/*
 * Rótulos das etapas. Repetem os de `CadastroAssistido.tsx` de propósito: são
 * duas telas com ciclos de vida diferentes, e exportar de uma para a outra
 * amarraria a lista global à tela da prova sem ganho.
 */
const ETAPA_LABEL: Record<string, string> = {
  extraction: 'Extração',
  answer_key: 'Gabarito',
  classification: 'Classificação',
};

const ETAPA_STATUS: Record<string, string> = {
  pending: 'pendente',
  running: 'rodando',
  succeeded: 'concluída',
  failed: 'falhou',
  skipped: 'pulada',
};

const EM_ANDAMENTO = ['queued', 'running'];

export function CadastroAssistidoPage() {
  const { ler, filtrar } = useEstadoDeLista();
  const aba = ler.texto('aba') ?? 'runs';

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Cadastro Assistido
        </Typography.Title>
        <Typography.Text type="secondary">
          A transcrição da prova é feita pelo Claude Code, pelo MCP. Nada é publicado: tudo entra como
          sugestão, para revisão humana.
        </Typography.Text>
      </div>

      <Tabs
        activeKey={aba}
        // A página é da tabela de processamentos: não sobrevive à ida para a
        // configuração.
        onChange={(chave) => filtrar({ aba: chave === 'runs' ? undefined : chave })}
        items={[
          { key: 'runs', label: 'Processamentos', children: <Processamentos /> },
          { key: 'config', label: 'Configuração', children: <Configuracao /> },
        ]}
      />
    </Space>
  );
}

/* ------------------------------------------------------- processamentos */

function Processamentos() {
  const navigate = useNavigate();
  const { ler, irParaPagina } = useEstadoDeLista();
  const page = ler.pagina();

  const [emDetalhe, setEmDetalhe] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ['ingestion-runs-todos', page],
    queryFn: () => api.listAllIngestionRuns({ page, pageSize: 20 }),
    // Enquanto houver run correndo, a lista se atualiza sozinha.
    refetchInterval: (consulta) =>
      consulta.state.data?.items.some((run) => EM_ANDAMENTO.includes(run.status)) ? 5000 : false,
  });

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Flex justify="space-between" align="center" wrap gap={12}>
        <Typography.Text type="secondary">
          Todos os processamentos, do mais recente ao mais antigo.
        </Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void runs.refetch()}>
            Atualizar
          </Button>
        </Space>
      </Flex>

      {runs.error ? <Alert type="error" showIcon title={(runs.error as Error).message} /> : null}

      {emDetalhe ? (
        <DetalheDoProcessamento id={emDetalhe} onFechar={() => setEmDetalhe(null)} />
      ) : null}

      <Table<IngestionRunRow>
        rowKey="id"
        size="small"
        loading={runs.isPending}
        dataSource={runs.data?.items ?? []}
        scroll={{ x: true }}
        onRow={(run) => ({
          style: { cursor: 'pointer' },
          onClick: () => setEmDetalhe(run.id),
        })}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nenhum processamento ainda. Eles nascem quando uma sessão do Claude Code abre uma prova."
            />
          ),
        }}
        pagination={{
          current: page,
          pageSize: runs.data?.pageSize ?? 20,
          total: runs.data?.total ?? 0,
          onChange: (pagina: number) => irParaPagina(pagina),
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        columns={[
          {
            title: 'Prova',
            dataIndex: 'examName',
            /*
             * Largura fixa de propósito. Sem ela, a mensagem longa da confirmação
             * de caderno empurrava a tabela e espremia esta coluna até o nome da
             * prova sair com uma letra por linha.
             */
            width: 280,
            render: (nome: string, run) => (
              <Space orientation="vertical" size={0}>
                <Typography.Link onClick={() => void navigate(`/provas/${run.examId}`)}>
                  {nome}
                </Typography.Link>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {MODULO_LABEL[run.examModule as ExamModule]}
                  {run.bookletType ? ` · caderno ${run.bookletType}` : ''}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: 'Situação',
            dataIndex: 'status',
            width: 300,
            render: (status: string, run) => (
              <Space orientation="vertical" size={4} style={{ maxWidth: 280 }}>
                <Tag color={STATUS_COR[status]}>{STATUS_LABEL[status] ?? status}</Tag>

                {run.errorMessage ? (
                  /*
                   * O motivo tem que estar à mão - run que para sem motivo
                   * visível vira mistério que ninguém investiga -, mas com
                   * largura limitada: o `ellipsis` sozinho não corta nada se o
                   * pai puder crescer, e era isso que quebrava a tabela.
                   */
                  <Tooltip title={run.errorMessage}>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12, maxWidth: 280 }}
                      ellipsis
                    >
                      {run.errorMessage}
                    </Typography.Text>
                  </Tooltip>
                ) : null}
              </Space>
            ),
          },
          {
            title: 'Campos pedidos',
            dataIndex: 'requestedFields',
            width: 200,
            render: (campos: string[]) => (
              <Space size={[4, 4]} wrap>
                {campos.map((campo) => (
                  <Tag key={campo} style={{ marginInlineEnd: 0 }}>
                    {rotuloDoCampo(campo)}
                  </Tag>
                ))}
              </Space>
            ),
          },
          {
            title: 'Custo',
            dataIndex: 'costUsd',
            width: 100,
            align: 'right',
            render: (custo: number) => `US$ ${custo.toFixed(4)}`,
          },
          {
            title: 'Acionado por',
            dataIndex: 'triggeredByName',
            width: 160,
            // Quem pediu o run, não o autor do conteúdo (D-17).
            render: (nome: string | null, run) =>
              run.triggeredBy ? (
                (nome ?? 'Conta excluída')
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: '',
            key: 'acoes',
            width: 60,
            align: 'right',
            render: (_, run) => <DescartarSugestoes runId={run.id} />,
          },
          {
            title: 'Quando',
            dataIndex: 'createdAt',
            width: 150,
            render: (data: string) => formatarData(data),
          },
        ]}
      />
    </Space>
  );
}

const MOTIVO_LABEL: Record<string, string> = {
  publicada: 'já publicada',
  tem_versao_humana: 'tem versão escrita por uma pessoa',
  de_outro_run: 'tem versão de outro processamento',
};

/**
 * Descarta as sugestões que ninguém aproveitou.
 *
 * A prévia é carregada **antes** de qualquer exclusão e mostrada na confirmação:
 * é uma remoção sem volta, e o administrador precisa ver quantas questões saem e
 * quantas ficam - com o motivo - antes de decidir. O processamento em si
 * permanece: ele é o registro do que foi pedido e do que se gastou.
 */
function DescartarSugestoes({ runId }: { runId: string }) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [carregando, setCarregando] = useState(false);

  const descartar = useMutation({
    mutationFn: () => api.discardRunSuggestions(runId),
    onSuccess: async ({ apagadas, preservadas }) => {
      message.success(
        preservadas > 0
          ? `${apagadas} sugestão(ões) apagada(s); ${preservadas} preservada(s).`
          : `${apagadas} sugestão(ões) apagada(s).`,
      );
      await queryClient.invalidateQueries({ queryKey: ['ingestion-runs-todos'] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const abrir = async () => {
    setCarregando(true);
    try {
      const previa = await api.previewDiscardRun(runId);

      if (previa.apagaveis.length === 0 && previa.preservadas.length === 0) {
        message.info('Este processamento não criou nenhuma questão.');
        return;
      }

      const porMotivo = previa.preservadas.reduce<Record<string, number>>((mapa, item) => {
        mapa[item.motivo] = (mapa[item.motivo] ?? 0) + 1;
        return mapa;
      }, {});

      modal.confirm({
        title: 'Descartar as sugestões deste processamento?',
        width: 520,
        okText: previa.apagaveis.length > 0 ? `Apagar ${previa.apagaveis.length}` : 'Fechar',
        okButtonProps: { danger: true, disabled: previa.apagaveis.length === 0 },
        cancelText: 'Cancelar',
        content: (
          <Space orientation="vertical" size={10} style={{ width: '100%', marginTop: 8 }}>
            <Typography.Text>
              <strong>{previa.apagaveis.length}</strong> questão(ões) serão apagadas, com todo o
              histórico. Não tem volta.
            </Typography.Text>

            {previa.preservadas.length > 0 ? (
              <Alert
                type="info"
                showIcon
                title={`${previa.preservadas.length} preservada(s)`}
                description={
                  <Space orientation="vertical" size={2}>
                    {Object.entries(porMotivo).map(([motivo, quantidade]) => (
                      <Typography.Text key={motivo} style={{ fontSize: 12 }}>
                        {quantidade} · {MOTIVO_LABEL[motivo] ?? motivo}
                      </Typography.Text>
                    ))}
                  </Space>
                }
              />
            ) : null}

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              O processamento continua na lista, com o custo e o que foi pedido.
            </Typography.Text>
          </Space>
        ),
        onOk: () => {
          if (previa.apagaveis.length > 0) descartar.mutate();
        },
      });
    } catch (erro) {
      message.error((erro as Error).message);
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Tooltip title="Descartar as sugestões que ninguém aproveitou">
      <Button
        type="text"
        danger
        size="small"
        icon={<DeleteOutlined />}
        loading={carregando || descartar.isPending}
        aria-label="Descartar sugestões deste processamento"
        onClick={() => void abrir()}
      />
    </Tooltip>
  );
}

/**
 * Tudo o que se sabe de um processamento.
 *
 * A linha da tabela cabe num relance; aqui cabe o que se precisa para entender
 * o que aconteceu - qual modelo rodou em cada etapa, quanto cada uma custou,
 * quanto tempo levou, e quantas questões saíram no fim. Sem isto, um run que
 * gastou caro ou demorou demais era um número solto na lista.
 */
function DetalheDoProcessamento({ id, onFechar }: { id: string; onFechar: () => void }) {
  const navigate = useNavigate();

  const detalhe = useQuery({
    queryKey: ['ingestion-run', id],
    queryFn: () => api.getIngestionRun(id),
    // Enquanto o run corre, o detalhe acompanha - é onde se olha esperando.
    refetchInterval: (consulta) =>
      consulta.state.data && EM_ANDAMENTO.includes(consulta.state.data.status) ? 3000 : false,
  });

  const run = detalhe.data;

  /*
   * Duração só do que terminou. Contar contra o relógio durante a renderização
   * é chamada impura - e não vale a pena: o modal já se refaz a cada três
   * segundos enquanto o run corre, então o número aparece assim que ele fecha.
   */
  const duracao = (inicio: string | null, fim: string | null): string => {
    if (!inicio) return '—';
    if (!fim) return 'em andamento';

    const segundos = Math.round((new Date(fim).getTime() - new Date(inicio).getTime()) / 1000);
    if (segundos < 60) return `${segundos} s`;
    return `${Math.floor(segundos / 60)} min ${segundos % 60} s`;
  };

  return (
    <Modal
      open
      width={780}
      title="Processamento"
      onCancel={onFechar}
      footer={[
        <Button key="fechar" onClick={onFechar}>
          Fechar
        </Button>,
        run && run.questionsTouched > 0 ? (
          <Button
            key="revisar"
            type="primary"
            onClick={() => void navigate(`/questoes?examId=${run.examId}&revisao=1`)}
          >
            Revisar sugestões
          </Button>
        ) : null,
      ]}
    >
      {detalhe.isPending ? (
        <Typography.Text type="secondary">Carregando...</Typography.Text>
      ) : detalhe.error ? (
        <Alert type="error" showIcon title={(detalhe.error as Error).message} />
      ) : run ? (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          {run.errorMessage ? (
            <Alert
              type={run.status === 'needs_confirmation' ? 'warning' : 'error'}
              showIcon
              title={
                run.status === 'needs_confirmation'
                  ? 'Aguardando confirmação'
                  : 'O processamento parou'
              }
              description={run.errorMessage}
            />
          ) : null}

          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Prova" span={2}>
              <Typography.Link onClick={() => void navigate(`/provas/${run.examId}`)}>
                {run.examName ?? run.examId}
              </Typography.Link>
            </Descriptions.Item>
            <Descriptions.Item label="Situação">
              <Tag color={STATUS_COR[run.status]}>{STATUS_LABEL[run.status] ?? run.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Caderno">{run.bookletType ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Campos pedidos" span={2}>
              <Space size={[4, 4]} wrap>
                {run.requestedFields.map((campo) => (
                  <Tag key={campo} style={{ marginInlineEnd: 0 }}>
                    {rotuloDoCampo(campo)}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Custo">
              {/* Teto ao lado do gasto: o número sozinho não diz se sobrou folga. */}
              US$ {run.costUsd.toFixed(4)} de US$ {Number(run.costLimitUsd).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Duração">
              {duracao(run.startedAt, run.finishedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Acionado por">
              {/* Quem pediu, e não quem escreveu: a máquina não é usuária (D-17). */}
              {run.triggeredByName ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Quando">{formatarData(run.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Questões geradas ou versionadas" span={2}>
              {run.questionsTouched}
            </Descriptions.Item>
          </Descriptions>

          {run.classificacao ? (
            <div>
              <Typography.Text strong>Classificação</Typography.Text>
              <Space orientation="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                {run.classificacao.semClassificacao > 0 ? (
                  /*
                   * Não é erro: a IA devolve vazio quando nada cadastrado serve,
                   * em vez de inventar termo (G-TAXONOMY). É tarefa - e só vira
                   * tarefa quando alguém vê o número.
                   */
                  <Alert
                    type="warning"
                    showIcon
                    title={`${run.classificacao.semClassificacao} questão(ões) sem assunto`}
                    description="A pipeline não inventa termo: quando nada cadastrado serve, devolve vazio. Cadastre o assunto em Cadastros e reprocesse."
                  />
                ) : (
                  <Typography.Text type="secondary">
                    Todas as questões receberam classificação.
                  </Typography.Text>
                )}

                {run.classificacao.porDisciplina.length > 0 ? (
                  <Space size={[6, 6]} wrap>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Disciplinas mais frequentes:
                    </Typography.Text>
                    {run.classificacao.porDisciplina.map((item) => (
                      <Tag key={item.name}>
                        {item.name} · {item.total}
                      </Tag>
                    ))}
                  </Space>
                ) : null}

                {run.classificacao.porDisciplina.length === 1 &&
                run.questionsTouched > 5 ? (
                  // Prova inteira numa disciplina só não levanta erro nenhum: as
                  // questões ficam classificadas, só que erradas.
                  <Alert
                    type="warning"
                    showIcon
                    title="Tudo caiu numa disciplina só"
                    description="Vale conferir por amostragem: prompt, taxonomia incompleta ou o modelo se agarrando ao primeiro termo da lista produzem exatamente isto, sem erro nenhum."
                  />
                ) : null}
              </Space>
            </div>
          ) : null}

          <div>
            <Typography.Text strong>Etapas</Typography.Text>
            <Table
              size="small"
              rowKey="id"
              style={{ marginTop: 8 }}
              dataSource={run.steps}
              pagination={false}
              locale={{ emptyText: 'Nenhuma etapa registrada ainda.' }}
              columns={[
                {
                  title: 'Etapa',
                  dataIndex: 'step',
                  render: (valor: string) => ETAPA_LABEL[valor] ?? valor,
                },
                {
                  title: 'Situação',
                  dataIndex: 'status',
                  width: 110,
                  render: (valor: string, etapa) =>
                    etapa.errorMessage ? (
                      <Tooltip title={etapa.errorMessage}>
                        <Tag color="red" style={{ cursor: 'help' }}>
                          {ETAPA_STATUS[valor] ?? valor}
                        </Tag>
                      </Tooltip>
                    ) : (
                      <Tag color={valor === 'succeeded' ? 'green' : undefined}>
                        {ETAPA_STATUS[valor] ?? valor}
                      </Tag>
                    ),
                },
                {
                  title: 'Modelo',
                  dataIndex: 'model',
                  render: (valor: string | null) => valor ?? '—',
                },
                {
                  title: 'Custo',
                  dataIndex: 'costUsd',
                  width: 100,
                  align: 'right',
                  render: (valor: string) => `US$ ${Number(valor).toFixed(4)}`,
                },
                {
                  title: 'Tempo',
                  width: 100,
                  align: 'right',
                  render: (_, etapa) => duracao(etapa.startedAt, etapa.finishedAt),
                },
              ]}
            />
          </div>
        </Space>
      ) : null}
    </Modal>
  );
}

/* ---------------------------------------------------------- configuração */

/**
 * O que restou de configurável, depois de o motor da nuvem sair.
 *
 * Os quatro modelos de texto e os prompts por etapa foram embora com ele: quem
 * transcreve agora é o Claude Code, e o modelo dele é escolhido na sessão. **A
 * voz fica** porque a narração continua passando pelo OpenRouter (D-21) - quem
 * espera por ela é o aluno, ao vivo, e isso não se pré-computa no terminal.
 */
function Configuracao() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['ingestion-settings'],
    queryFn: () => api.getIngestionSettings(),
  });

  /*
   * A lista vem do próprio gateway, filtrada por saída de áudio: escolher daqui
   * evita o modelo inexistente que quebrou a narração antes.
   */
  const modelosDeFala = useQuery({
    queryKey: ['speech-models'],
    queryFn: () => api.listSpeechModels(),
  });

  const [voz, setVoz] = useState<string | undefined>();
  const [teto, setTeto] = useState<number | null>(null);

  const salvar = useMutation({
    mutationFn: () =>
      api.updateIngestionSettings({
        ...(voz !== undefined ? { modelTts: voz || null } : {}),
        ...(teto !== null ? { costLimitUsd: teto } : {}),
      }),
    onSuccess: async () => {
      message.success('Configuração salva.');
      setVoz(undefined);
      setTeto(null);
      await queryClient.invalidateQueries({ queryKey: ['ingestion-settings'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const dados = config.data;
  const temMudanca = voz !== undefined || teto !== null;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small" title="Voz da narração" loading={config.isPending}>
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Flex align="center" gap={8}>
            <Typography.Text strong>Modelo de fala</Typography.Text>
            {dados && !dados.personalizado.modelTts ? (
              <Tag>padrão</Tag>
            ) : (
              <Tag color="blue">personalizado</Tag>
            )}
          </Flex>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 6, fontSize: 12 }}>
            Usado para narrar as páginas da Legislação Comentada. Repare que nem tudo que sai como
            áudio é fala: os modelos Lyria geram música.
          </Typography.Paragraph>
          <Space.Compact style={{ width: '100%', maxWidth: 560 }}>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={modelosDeFala.isPending}
              value={(voz !== undefined ? voz : dados?.modelTts) || undefined}
              placeholder="Escolha o modelo de fala"
              notFoundContent={
                modelosDeFala.isPending
                  ? 'Carregando...'
                  : 'O gateway não lista nenhum modelo com saída de áudio.'
              }
              onChange={setVoz}
              options={(modelosDeFala.data ?? []).map((modelo) => ({
                value: modelo.id,
                label: modelo.name,
              }))}
            />
            <Tooltip title="Volta ao modelo padrão do ambiente">
              <Button
                disabled={dados ? !dados.personalizado.modelTts && voz === undefined : true}
                onClick={() => setVoz('')}
              >
                Padrão
              </Button>
            </Tooltip>
          </Space.Compact>
        </Space>
      </Card>

      <Card size="small" title="Teto de gasto por processamento" loading={config.isPending}>
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          {/*
            **O teto virou registro, e a tela precisa dizer isso.** Enquanto
            havia um gateway entre o modelo e nós, o run parava ao atingir o
            valor; com o Claude Code não há onde interromper antes da chamada, e
            prometer interrupção aqui seria a pior espécie de rótulo - o que
            descreve uma garantia que não existe mais.
          */}
          <Typography.Text type="secondary">
            Fica gravado em cada processamento como o teto que valia quando ele começou. É o que
            permite investigar um gasto depois de a configuração ter mudado.
          </Typography.Text>
          <Space align="center">
            <InputNumber
              addonBefore="US$"
              min={0.01}
              max={1000}
              step={0.5}
              style={{ width: 180 }}
              value={teto ?? dados?.costLimitUsd}
              onChange={(valor) => setTeto(valor ?? null)}
            />
            {dados?.personalizado.costLimitUsd ? (
              <Tag color="blue">personalizado</Tag>
            ) : (
              <Tag>padrão</Tag>
            )}
          </Space>
        </Space>
      </Card>

      <Flex justify="flex-end">
        <Button
          type="primary"
          disabled={!temMudanca}
          loading={salvar.isPending}
          onClick={() => salvar.mutate()}
        >
          Salvar configuração
        </Button>
      </Flex>
    </Space>
  );
}

const rotuloDoCampo = (campo: string): string =>
  ({
    statement: 'Enunciado',
    options: 'Itens',
    correctOption: 'Gabarito',
    classification: 'Classificação',
    label: 'Rótulo',
    essayKind: 'Parte',
    answerKey: 'Espelho',
  })[campo] ?? campo;
